/**
 * Type + Kind step components for AccountFormWizard. Extracted to a sibling
 * file to keep AccountFormWizard.tsx under the max-lines threshold.
 * Not part of the public API.
 */

import { useRef, useEffect } from 'react'

import {
  AlertTriangle,
  BadgeAlert,
  Banknote,
  CheckCircle2,
  Percent,
  Scale,
  TrendingDown,
  TrendingUp,
} from 'lucide-react'

import { KbdHint, TypeCard } from './AccountFormWizard-parts'
import { type AccountIntent } from './AccountFormWizard-steps'
import { type AccountFormData } from './chart-of-accounts-helpers'
import { ACCOUNT_TYPE_LABELS } from '../../constants/labels'

import type { AccountType } from '../../services/account-service'

const ACCOUNT_TYPE_OPTIONS = Object.entries(ACCOUNT_TYPE_LABELS).map(([value, label]) => ({ value, label }))

const TYPE_DESCRIPTIONS: Record<AccountType, string> = {
  ACCOUNT_TYPE_ASSET: 'Cash, bank, receivables, fixed assets, investments',
  ACCOUNT_TYPE_LIABILITY: 'Loans, payables, accruals, provisions',
  ACCOUNT_TYPE_EQUITY: 'Owner equity, capital, retained earnings',
  ACCOUNT_TYPE_REVENUE: 'Sales, service revenue, interest income',
  ACCOUNT_TYPE_EXPENSE: 'Operating costs, salaries, utilities, depreciation',
}

interface KindOption { value: AccountIntent; label: string; description: string; icon: React.ReactNode }

const KIND_OPTIONS: KindOption[] = [
  { value: 'cash',    label: 'Cash / Petty Cash', description: 'Physical cash on hand, petty cash funds',                    icon: <Banknote className="w-4 h-4" /> },
  { value: 'bank',    label: 'Bank Account',      description: 'Current, savings, overdraft, cash credit, or fixed deposit', icon: <Banknote className="w-4 h-4" /> },
  { value: 'general', label: 'Other Asset',       description: 'Fixed assets, prepayments, investments, other receivables',  icon: <Scale className="w-4 h-4" /> },
]

function makeArrowHandler<T>(options: T[], current: T, onChange: (v: T) => void) {
  return (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const idx = options.indexOf(current)
    const isFwd = e.key === 'ArrowDown' || e.key === 'ArrowRight'
    const isBack = e.key === 'ArrowUp' || e.key === 'ArrowLeft'
    if (!isFwd && !isBack) {return}
    e.preventDefault()
    const nextIdx = isFwd
      ? (idx + 1) % options.length
      : (idx - 1 + options.length) % options.length
    const nextVal = options[nextIdx]
    if (nextVal !== undefined) {onChange(nextVal)}
    const btns = e.currentTarget.closest('[role="radiogroup"]')
      ?.querySelectorAll<HTMLButtonElement>('[role="radio"]')
    btns?.[nextIdx]?.focus()
  }
}

function typeIcon(t: AccountType): React.ReactNode {
  const cls = 'w-4 h-4'
  switch (t) {
    case 'ACCOUNT_TYPE_ASSET':     return <Banknote className={cls} />
    case 'ACCOUNT_TYPE_LIABILITY': return <AlertTriangle className={cls} />
    case 'ACCOUNT_TYPE_EQUITY':    return <Scale className={cls} />
    case 'ACCOUNT_TYPE_REVENUE':   return <TrendingUp className={cls} />
    case 'ACCOUNT_TYPE_EXPENSE':   return <TrendingDown className={cls} />
  }
}

// ── TypeStep ──────────────────────────────────────────────────────────────────

export interface TypeStepProps {
  formData: AccountFormData
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
  setIntent: (v: AccountIntent) => void
  isGstMode: boolean
  setIsGstMode: (v: boolean) => void
}

function pickAccountType(
  value: AccountType,
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>,
  setIntent: (v: AccountIntent) => void,
  setIsGstMode: (v: boolean) => void,
): void {
  setFormData(prev => ({
    ...prev, accountType: value, parentId: '', code: '', accountSubtype: '',
    gstComponent: '', gstDirection: '', gstRate: '', systemTag: '',
  }))
  setIntent('general')
  setIsGstMode(false)
}

function GstTypeCard({ isGstMode, setIsGstMode, setFormData }: {
  isGstMode: boolean
  setIsGstMode: (v: boolean) => void
  setFormData: React.Dispatch<React.SetStateAction<AccountFormData>>
}): JSX.Element {
  return (
    <TypeCard
      aria-checked={isGstMode}
      description="A per-rate ledger under an existing consolidated GST book, e.g. CGST Output @9%"
      icon={<Percent className="w-4 h-4" />}
      isSelected={isGstMode}
      label="GST Ledger"
      shortcutKey="6"
      tabIndex={isGstMode ? 0 : -1}
      onClick={() => {
        setIsGstMode(true)
        setFormData(prev => ({ ...prev, gstComponent: prev.gstComponent || 'cgst', gstDirection: prev.gstDirection || 'output' }))
      }}
    />
  )
}

export function TypeStep({ formData, setFormData, setIntent, isGstMode, setIsGstMode }: TypeStepProps): JSX.Element {
  const groupRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      groupRef.current?.querySelector<HTMLButtonElement>('[role="radio"][tabindex="0"]')?.focus()
    })
    return () => cancelAnimationFrame(raf)
  }, [])

  const typeValues = ACCOUNT_TYPE_OPTIONS.map(o => o.value as AccountType)
  const typeArrow = makeArrowHandler(typeValues, formData.accountType, (v) => {
    pickAccountType(v, setFormData, setIntent, setIsGstMode)
  })

  return (
    <div className="flex flex-col gap-5">
      <div>
        <p className="text-sm font-semibold text-foreground mb-3">Account type</p>
        <div ref={groupRef} aria-label="Account type" className="grid grid-cols-1 gap-2" role="radiogroup">
          {ACCOUNT_TYPE_OPTIONS.map(({ value, label }, idx) => (
            <TypeCard
              key={value}
              aria-checked={!isGstMode && formData.accountType === value}
              description={TYPE_DESCRIPTIONS[value as AccountType]}
              icon={typeIcon(value as AccountType)}
              isSelected={!isGstMode && formData.accountType === value}
              label={label}
              shortcutKey={String(idx + 1)}
              tabIndex={!isGstMode && formData.accountType === value ? 0 : -1}
              onClick={() => pickAccountType(value as AccountType, setFormData, setIntent, setIsGstMode)}
              onKeyDown={typeArrow}
            />
          ))}
          <GstTypeCard isGstMode={isGstMode} setFormData={setFormData} setIsGstMode={setIsGstMode} />
        </div>
        <p className="text-xs text-muted-foreground mt-2.5">
          Tip: press <KbdHint>1</KbdHint>–<KbdHint>6</KbdHint> to select · <KbdHint>↑↓</KbdHint> arrows to navigate · <KbdHint>Enter</KbdHint> to continue
        </p>
      </div>
      {!isGstMode && <TypeInfoBlock accountType={formData.accountType} />}
    </div>
  )
}

function TypeInfoBlock({ accountType }: { accountType: AccountType }): JSX.Element {
  if (accountType === 'ACCOUNT_TYPE_LIABILITY') {
    return (
      <div className="el-edge flex items-start gap-2.5 p-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30">
        <BadgeAlert className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
        <p className="text-xs text-blue-700 dark:text-blue-400 leading-relaxed">
          <strong>Supplier Payable (AP)</strong> sub-ledgers are auto-created when you add a supplier in{' '}
          <strong>Parties → Suppliers</strong>. This form creates general liability accounts (loans, accruals, provisions, etc.).
        </p>
      </div>
    )
  }
  const text: Record<string, string> = {
    ACCOUNT_TYPE_ASSET:   "Next you'll choose whether this is Cash, a Bank Account, or another asset type. Accounts Receivable is managed automatically via Parties → Customers.",
    ACCOUNT_TYPE_EQUITY:  "Equity accounts track the owners' stake — capital contributed, retained earnings, and drawings.",
    ACCOUNT_TYPE_REVENUE: 'Revenue accounts record income from sales, services, interest, and other earnings.',
    ACCOUNT_TYPE_EXPENSE: 'Expense accounts record what the organisation spends — salaries, utilities, materials, and depreciation.',
  }
  return (
    <div className="el-edge flex items-start gap-2.5 p-3 rounded-lg border border-muted bg-muted/30">
      <CheckCircle2 className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
      <p className="text-xs text-muted-foreground leading-relaxed">{text[accountType]}</p>
    </div>
  )
}

// ── KindStep ──────────────────────────────────────────────────────────────────

export interface KindStepProps {
  intent: AccountIntent
  setIntent: (v: AccountIntent) => void
}

export function KindStep({ intent, setIntent }: KindStepProps): JSX.Element {
  const groupRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      groupRef.current?.querySelector<HTMLButtonElement>('[role="radio"][tabindex="0"]')?.focus()
    })
    return () => cancelAnimationFrame(raf)
  }, [])

  const kindValues = KIND_OPTIONS.map(o => o.value)
  const kindArrow = makeArrowHandler(kindValues, intent, setIntent)

  return (
    <div className="flex flex-col gap-5">
      <div>
        <p className="text-sm font-semibold text-foreground mb-3">What kind of asset?</p>
        <div ref={groupRef} aria-label="Asset kind" className="grid grid-cols-1 gap-2" role="radiogroup">
          {KIND_OPTIONS.map(({ value, label, description, icon }, idx) => (
            <TypeCard
              key={value}
              aria-checked={intent === value}
              description={description}
              icon={icon}
              isSelected={intent === value}
              label={label}
              shortcutKey={String(idx + 1)}
              tabIndex={intent === value ? 0 : -1}
              onClick={() => setIntent(value)}
              onKeyDown={kindArrow}
            />
          ))}
          <ReceivableNotice />
        </div>
        <p className="text-xs text-muted-foreground mt-2.5">
          Tip: press <KbdHint>1</KbdHint>–<KbdHint>3</KbdHint> to select · <KbdHint>↑↓</KbdHint> arrows to navigate · <KbdHint>Enter</KbdHint> to continue
        </p>
      </div>
    </div>
  )
}

function ReceivableNotice(): JSX.Element {
  return (
    <div
      aria-disabled
      aria-label="Customer Receivable — managed via Parties"
      aria-selected="false"
      className="el-edge flex items-start gap-3 p-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30"
      role="option"
    >
      <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Customer Receivable (AR)</p>
        <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
          Sub-ledgers are created automatically when you add a customer. Go to <strong>Parties → Customers</strong> to add one.
        </p>
      </div>
    </div>
  )
}

// Exported number-key shortcut maps for the wizard's keyboard handler
export const TYPE_SHORTCUT_MAP: Partial<Record<string, AccountType>> = {
  '1': 'ACCOUNT_TYPE_ASSET',
  '2': 'ACCOUNT_TYPE_LIABILITY',
  '3': 'ACCOUNT_TYPE_EQUITY',
  '4': 'ACCOUNT_TYPE_REVENUE',
  '5': 'ACCOUNT_TYPE_EXPENSE',
}

export const KIND_VALUES: AccountIntent[] = KIND_OPTIONS.map(o => o.value)
